import React from 'react';
import { Country } from '../../types/todo';
import { Globe2 } from 'lucide-react';

interface CountriesGridProps {
  countries: Country[];
  isLoading: boolean;
}

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [isInView, setIsInView] = React.useState(false);
  React.useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.05 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return isInView;
}

export function CountriesGrid({ countries, isLoading }: CountriesGridProps) {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef);

  const getOptimizedImageUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('unsplash.com')) {
        const baseUrl = url.split('?')[0];
        return `${baseUrl}?q=80&w=800&h=600&fit=crop&crop=entropy&auto=format`;
      }
      return url;
    } catch {
      return url;
    }
  };

  return (
    <section ref={sectionRef} className="py-16 sm:py-24 bg-white dark:bg-gray-800 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-sm font-medium mb-4">
            <Globe2 className="w-4 h-4" />
            <span>{countries.length} destinations</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            O&ugrave; partez-vous ?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Chaque destination a sa checklist d&eacute;di&eacute;e, avec les d&eacute;marches sp&eacute;cifiques &agrave; votre pays.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent-500 border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {countries.map((country, index) => (
              <div
                key={country.code}
                className={`group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-default transition-all duration-500 ${
                  isInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <img
                  src={getOptimizedImageUrl(country.image)}
                  alt={country.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                <div className="absolute inset-0 flex flex-col justify-end p-3 sm:p-5">
                  <div className="flex items-center gap-2">
                    <span className="text-xl sm:text-2xl drop-shadow-lg">{country.flag}</span>
                    <h3 className="text-sm sm:text-lg font-bold text-white drop-shadow-lg">
                      {country.name}
                    </h3>
                  </div>
                </div>

                <div className="absolute inset-0 border border-white/10 rounded-2xl pointer-events-none" />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
